import { Audit, AuditTemplate } from '@/firebase/auditService';
import { ComplianceTemplate } from '@/firebase/complianceService';
import { complianceService } from '@/firebase/complianceService';
import { getAllTasks, TaskWithDetails, Task } from '@/services/taskService';
import { taskCompletionService } from '@/firebase/taskCompletionService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { Timestamp } from 'firebase/firestore';

export interface AuditReadiness {
  auditId: string;
  overallScore: number; // 0-100
  
  // Breakdown
  taskCompletion: {
    totalTasks: number;
    completedTasks: number;
    overdueTasks: number;
    score: number; // 0-100
  };
  
  evidenceAvailability: {
    requiredEvidenceTypes: number;
    availableEvidenceTypes: number;
    score: number; // 0-100
  };
  
  prerequisiteAudits: {
    required: boolean;
    completed: boolean;
    score: number; // 0 or 100
  };
  
  // Missing items
  missingTasks: Array<{
    taskId: string;
    taskName: string;
    dueDate: Date | null;
    isOverdue: boolean;
  }>;
  missingEvidenceTypes: Array<{
    templateId: string;
    templateName: string;
    evidenceType: string;
    evidenceLabel: string;
    required: boolean;
  }>;
  missingPrerequisites: string[]; // Audit IDs
  
  calculatedAt: Date;
}

export interface ReadinessCalculationOptions {
  includeOverdue?: boolean; // Whether to include overdue tasks in calculations
}

/**
 * Calculate audit readiness score
 * 
 * Formula:
 * - Task Completion: 50% weight
 * - Evidence Availability: 30% weight  
 * - Prerequisite Audits: 20% weight
 */
export const auditReadinessService = {
  /**
   * Calculate readiness for an audit
   */
  calculateReadiness: async (
    audit: Audit,
    options: ReadinessCalculationOptions = {}
  ): Promise<AuditReadiness> => {
    const { includeOverdue = true } = options;
    
    // Get all compliance templates for this audit
    const complianceTemplates = await getComplianceTemplates(audit.complianceTemplateIds || []);
    
    // Calculate task completion
    const taskCompletion = await calculateTaskCompletion(
      audit,
      complianceTemplates,
      includeOverdue
    );
    
    // Calculate evidence availability
    const evidenceAvailability = await calculateEvidenceAvailability(
      audit,
      complianceTemplates
    );
    
    // Check prerequisite audits
    const prerequisiteAudits = await checkPrerequisiteAudits(audit);
    
    // Calculate overall score (weighted average)
    const overallScore = Math.round(
      taskCompletion.score * 0.5 +      // 50% weight
      evidenceAvailability.score * 0.3 +  // 30% weight
      prerequisiteAudits.score * 0.2     // 20% weight
    );
    
    return {
      auditId: audit.id || '',
      overallScore: Math.max(0, Math.min(100, overallScore)), // Clamp between 0-100
      taskCompletion,
      evidenceAvailability,
      prerequisiteAudits,
      missingTasks: taskCompletion.missingTasks || [],
      missingEvidenceTypes: evidenceAvailability.missingEvidenceTypes || [],
      missingPrerequisites: prerequisiteAudits.missingPrerequisites || [],
      calculatedAt: new Date()
    };
  },
  
  /**
   * Calculate and update readiness score on an audit
   */
  calculateAndUpdateReadiness: async (
    audit: Audit,
    options?: ReadinessCalculationOptions
  ): Promise<AuditReadiness> => {
    const readiness = await auditReadinessService.calculateReadiness(audit, options);
    
    // Update audit with readiness score (if audit service supports it)
    // This would be called after task/evidence changes
    // For now, we'll just return the readiness
    
    return readiness;
  }
};

/**
 * Get compliance templates by IDs
 */
async function getComplianceTemplates(templateIds: string[]): Promise<ComplianceTemplate[]> {
  if (templateIds.length === 0) return [];
  
  const templates: ComplianceTemplate[] = [];
  
  for (const templateId of templateIds) {
    try {
      const result = await complianceService.getTemplate(templateId);
      if (result.success && result.data) {
        templates.push(result.data);
      }
    } catch (error) {
      console.error(`Error fetching compliance template ${templateId}:`, error);
    }
  }
  
  return templates;
}

/**
 * Calculate task completion score
 * Filters tasks by audit period based on frequency:
 * - Yearly: last 12 months from audit due date
 * - Semi-yearly: last 6 months
 * - Quarterly: last 3 months
 */
async function calculateTaskCompletion(
  audit: Audit,
  complianceTemplates: ComplianceTemplate[],
  includeOverdue: boolean
): Promise<{
  totalTasks: number;
  completedTasks: number;
  overdueTasks: number;
  score: number;
  missingTasks?: Array<{
    taskId: string;
    taskName: string;
    dueDate: Date | null;
    isOverdue: boolean;
  }>;
}> {
  if (!audit.locationId || !audit.organizationId) {
    return {
      totalTasks: 0,
      completedTasks: 0,
      overdueTasks: 0,
      score: 0,
      missingTasks: []
    };
  }
  
  // Calculate audit period start date based on frequency
  const auditDueDate = audit.dueDate?.toDate 
    ? audit.dueDate.toDate() 
    : (audit.dueDate ? new Date(audit.dueDate) : new Date());
  
  // Determine period in months based on audit frequency
  const periodMonths = audit.frequency === 'yearly' ? 12 :
                       audit.frequency === 'semi-yearly' ? 6 :
                       audit.frequency === 'quarterly' ? 3 : 12; // Default to yearly
  
  // Calculate period start date (go back X months from due date)
  const periodStartDate = new Date(auditDueDate);
  periodStartDate.setMonth(periodStartDate.getMonth() - periodMonths);
  periodStartDate.setHours(0, 0, 0, 0); // Start of day
  
  // Query tasks directly from Firestore for this location and organization
  const tasksRef = collection(db, 'tasks');
  const tasksQuery = query(
    tasksRef,
    where('locationId', '==', audit.locationId),
    where('organizationId', '==', audit.organizationId)
  );
  const tasksSnapshot = await getDocs(tasksQuery);
  
  const tasks: Task[] = [];
  tasksSnapshot.forEach((doc) => {
    tasks.push({ id: doc.id, ...doc.data() } as Task);
  });
  
  // Filter tasks that match audit's compliance template IDs OR are manually attached
  // Include ALL relevant tasks (they're required for the audit)
  // Date filtering only applies to whether completed tasks count as "completed"
  const relevantTasks = tasks.filter(task => {
    const matchesTemplate = audit.complianceTemplateIds?.includes(task.templateId) || false;
    const isManuallyAttached = audit.associatedTaskIds?.includes(task.id) || false;
    
    // Include if matches template or is manually attached
    return matchesTemplate || isManuallyAttached;
  });
  
  // Helper function to check if a completed task's completion date is within audit period
  const isCompletedWithinPeriod = (task: Task): boolean => {
    if (task.status !== 'completed' || !task.completedAt) {
      return false;
    }
    
    let completedDate: Date | null = null;
    
    // Handle Firestore Timestamp
    if (task.completedAt && typeof task.completedAt === 'object' && 'toDate' in task.completedAt && typeof task.completedAt.toDate === 'function') {
      completedDate = task.completedAt.toDate();
    } 
    // Handle Date object or timestamp number
    else if (task.completedAt) {
      completedDate = task.completedAt instanceof Date 
        ? task.completedAt 
        : new Date(task.completedAt);
    }
    
    if (completedDate) {
      completedDate.setHours(0, 0, 0, 0);
      return completedDate >= periodStartDate && completedDate <= auditDueDate;
    }
    
    return false;
  };
  
  // Enrich tasks with template names (simplified - just get template name from templateId)
  const enrichedTasks: Array<Task & { templateName: string }> = [];
  for (const task of relevantTasks) {
    let templateName = 'Unknown Task';
    try {
      const templateResult = await complianceService.getTemplate(task.templateId);
      if (templateResult.success && templateResult.data) {
        templateName = templateResult.data.name;
      }
    } catch (error) {
      console.error(`Error fetching template ${task.templateId}:`, error);
    }
    enrichedTasks.push({ ...task, templateName });
  }
  
  const totalTasks = enrichedTasks.length;
  // Only count tasks as "completed" if they were completed within the audit period
  const completedTasks = enrichedTasks.filter(task => 
    task.status === 'completed' && isCompletedWithinPeriod(task)
  ).length;
  const overdueTasks = enrichedTasks.filter(task => task.status === 'overdue').length;
  
  // Calculate score
  const score = totalTasks > 0 
    ? Math.round((completedTasks / totalTasks) * 100)
    : 100; // If no tasks required, consider it complete
  
  // Identify missing tasks (pending, overdue, or completed outside period)
  const missingTasks = enrichedTasks
    .filter(task => task.status !== 'completed' || !isCompletedWithinPeriod(task))
    .map(task => {
      const dueDate = task.dueDate?.toDate ? task.dueDate.toDate() : 
                     (task.dueDate ? new Date(task.dueDate) : null);
      const isOverdue = task.status === 'overdue' || 
                       (dueDate && dueDate < new Date() && task.status !== 'completed');
      
      return {
        taskId: task.id,
        taskName: task.templateName || 'Unknown Task',
        dueDate,
        isOverdue
      };
    });
  
  return {
    totalTasks,
    completedTasks,
    overdueTasks,
    score,
    missingTasks
  };
}

/**
 * Calculate evidence availability score
 */
async function calculateEvidenceAvailability(
  audit: Audit,
  complianceTemplates: ComplianceTemplate[]
): Promise<{
  requiredEvidenceTypes: number;
  availableEvidenceTypes: number;
  score: number;
  missingEvidenceTypes?: Array<{
    templateId: string;
    templateName: string;
    evidenceType: string;
    evidenceLabel: string;
    required: boolean;
  }>;
}> {
  if (!audit.locationId || !audit.organizationId) {
    return {
      requiredEvidenceTypes: 0,
      availableEvidenceTypes: 0,
      score: 100,
      missingEvidenceTypes: []
    };
  }
  
  // Collect all required evidence types from compliance templates
  const requiredEvidenceTypes: Array<{
    templateId: string;
    templateName: string;
    evidenceType: string;
    evidenceLabel: string;
    required: boolean;
  }> = [];
  
  complianceTemplates.forEach(template => {
    if (template.evidence_types && template.evidence_types.length > 0) {
      template.evidence_types.forEach(evidenceType => {
        // Only count required evidence types (required is true by default if not specified)
        const isRequired = evidenceType.required === undefined || evidenceType.required === true;
        if (isRequired) {
          requiredEvidenceTypes.push({
            templateId: template.id || '',
            templateName: template.name,
            evidenceType: evidenceType.type || '',
            evidenceLabel: evidenceType.label || '',
            required: true
          });
        }
      });
    }
  });
  
  const totalRequired = requiredEvidenceTypes.length;
  
  // Check which evidence types have evidence available
  // This is a simplified check - in a real implementation, you'd check:
  // 1. Task completions with evidence
  // 2. Evidence vault entries
  // 3. Direct evidence uploads
  
  // For now, we'll check task completions for this location
  let availableCount = 0;
  const availableEvidenceTypes = new Set<string>();
  
  // Calculate audit period start date based on frequency (same logic as task completion)
  const auditDueDate = audit.dueDate?.toDate 
    ? audit.dueDate.toDate() 
    : (audit.dueDate ? new Date(audit.dueDate) : new Date());
  
  const periodMonths = audit.frequency === 'yearly' ? 12 :
                       audit.frequency === 'semi-yearly' ? 6 :
                       audit.frequency === 'quarterly' ? 3 : 12;
  
  const periodStartDate = new Date(auditDueDate);
  periodStartDate.setMonth(periodStartDate.getMonth() - periodMonths);
  periodStartDate.setHours(0, 0, 0, 0);
  
  try {
    // Get task completions for this location
    const completionsResult = await taskCompletionService.getAllCompletions(
      audit.organizationId,
      [audit.locationId]
    );
    
    if (completionsResult.success && completionsResult.data) {
      const completions = completionsResult.data;
      
      // Filter completions by audit period
      const periodCompletions = completions.filter(completion => {
        if (!completion.completionDate) return false;
        
        let completionDate: Date | null = null;
        
        // Handle Firestore Timestamp
        if (completion.completionDate && typeof completion.completionDate === 'object' && 'toDate' in completion.completionDate && typeof completion.completionDate.toDate === 'function') {
          completionDate = completion.completionDate.toDate();
        } 
        // Handle Date object or timestamp number
        else if (completion.completionDate) {
          completionDate = completion.completionDate instanceof Date 
            ? completion.completionDate 
            : new Date(completion.completionDate);
        }
        
        if (!completionDate) return false;
        
        completionDate.setHours(0, 0, 0, 0);
        // Only include completions within the audit period
        return completionDate >= periodStartDate && completionDate <= auditDueDate;
      });
      
      // Create a map of templateId -> has evidence
      const templateEvidenceMap = new Map<string, boolean>();
      
      // Check each completion for evidence (only from period completions)
      periodCompletions.forEach(completion => {
        // Find the template ID for this completion
        const matchingTemplate = complianceTemplates.find(t => 
          t.name === completion.templateName || 
          t.id === (completion as any).templateId
        );
        
        if (matchingTemplate && completion.evidenceUrls && completion.evidenceUrls.length > 0) {
          // This task completion has evidence for this template
          templateEvidenceMap.set(matchingTemplate.id || '', true);
        }
      });
      
      // Count how many required evidence types have evidence
      requiredEvidenceTypes.forEach(req => {
        if (templateEvidenceMap.get(req.templateId)) {
          availableEvidenceTypes.add(`${req.templateId}-${req.evidenceType}`);
        }
      });
      
      availableCount = availableEvidenceTypes.size;
    }
  } catch (error) {
    console.error('Error checking evidence availability:', error);
  }
  
  // Calculate score
  const score = totalRequired > 0
    ? Math.round((availableCount / totalRequired) * 100)
    : 100; // If no evidence required, consider it complete
  
  // Identify missing evidence types
  const missingEvidenceTypes = requiredEvidenceTypes.filter(req => {
    // Simplified check - in real implementation, verify actual evidence exists
    return !availableEvidenceTypes.has(`${req.templateName}-evidence`);
  });
  
  return {
    requiredEvidenceTypes: totalRequired,
    availableEvidenceTypes: availableCount,
    score,
    missingEvidenceTypes
  };
}

/**
 * Check prerequisite audits
 */
async function checkPrerequisiteAudits(
  audit: Audit
): Promise<{
  required: boolean;
  completed: boolean;
  score: number;
  missingPrerequisites?: string[];
}> {
  // For now, we'll assume no prerequisites are required
  // In a real implementation, you'd check:
  // 1. If audit template has prerequisite audit types
  // 2. If previous audits of those types are completed for this location
  
  // This is a placeholder - implement based on your business logic
  return {
    required: false,
    completed: true,
    score: 100,
    missingPrerequisites: []
  };
}

